import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./ImprovementProjects.module.scss";
import Table from "../../components/Table/Table";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import MainLayout from "../../layouts/MainLayout/MainLayout";
import classNames from "classnames";
import Input from "../../components/Input/Input";
import { ReactComponent as SearchIcon } from "../../icons/search.svg";
import MultiSelect from "../../components/MultiSelect/MultiSelect";
import { formatDateSlash, handleExport } from "../../utils/helpers";
import { showModal } from "../../redux/modalStore";
import { useDispatch, useSelector } from "react-redux";
import DateRangeFilter from "../../components/DateRangeFilter/DateFIlterRange";
import useFetchOrganizationData from "../../actions/useFetchOrganizationData";
import Button from "../../components/Button/Button";
import axiosInstance from "../../utils/utils";
import { getOrganizationData } from "../../actions/organization";
import Swal from "sweetalert2";
import BulkEditModal from "../../modals/BulkEditModal/BulkEditModal";
import {
  resetSelection,
  clearResetSelection,
} from "../../redux/rowSelectionStore";
import ConfirmationPopup from "../../modals/ConfirmationPopup";
import ArchiveIllustration from "../../icons/archive-illustration.svg";
import { hideOverlay, showOverlay } from "../../redux/overlayLoaderStore";
import { setMeMode } from "../../redux/filterStore";
import { useTranslation } from "react-i18next";
import CreateImprovementProjectModal from "../../modals/CreateImprovementProjectModal";
import { Chart } from "chart.js/auto";
import {
  AlertCircle,
  Archive,
  CheckCircle2,
  Clock,
  Edit2,
  FileText,
  Plus,
  TrendingUp,
} from "lucide-react";

const ImprovementProjects = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();

  const sessionToken = localStorage.getItem("session_token");
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") === "all" ? "all" : "overview";

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedResponsibles, setSelectedResponsibles] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedRisks, setSelectedRisks] = useState([]);
  const [dateRange, setDateRange] = useState([null, null]);
  const [loading, setLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState("");
  const [actionToast, setActionToast] = useState(null);
  const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
  const [selectedImprovementProjects, setSelectedImprovementProjects] =
    useState([]);

  const actionToastTimerRef = useRef(null);

  const { meModeActive } = useSelector((state) => state.filters);
  const currentUser = useSelector((state) => state.user.email);
  const { improvementsData, findingsData } = useSelector(
    (state) => state.organization,
  );
  const resetRowSelection = useSelector(
    (state) => state.rowSelection.resetSelection,
  );

  const improvementProjectsData = useMemo(
    () => Object.values(improvementsData || {}),
    [improvementsData],
  );
  const visibleImprovementProjects = useMemo(
    () => improvementProjectsData.filter((project) => !project.archived),
    [improvementProjectsData],
  );

  console.log(improvementProjectsData, 'improvementProjectsData');

  
  useFetchOrganizationData();

  useEffect(() => {
    return () => {
      if (actionToastTimerRef.current) {
        window.clearTimeout(actionToastTimerRef.current);
        actionToastTimerRef.current = null;
      }
    };
  }, []);

  const showActionToast = (title, text) => {
    setActionToast({ title, text });

    if (actionToastTimerRef.current) {
      window.clearTimeout(actionToastTimerRef.current);
    }

    actionToastTimerRef.current = window.setTimeout(() => {
      setActionToast(null);
      actionToastTimerRef.current = null;
    }, 3500);
  };

  // UseEffect for enabling/disabling me mode when navigating through kanban columns links
  useEffect(() => {
    if (location.state?.fromKanban) {
      dispatch(setMeMode(true));
    }

    return () => {
      if (location.state?.fromKanban) {
        dispatch(setMeMode(false));
      }
    };
  }, []);

  useEffect(() => {
    const rawStatuses = searchParams.getAll("status");
    const statusesFromUrl = rawStatuses.every((status) => !status.includes(","))
      ? rawStatuses
      : [];
    setSelectedStatuses(statusesFromUrl);
  }, [searchParams]);

  useEffect(() => {
    const rawRisks = searchParams.getAll("risk");
    const risksFromUrl = rawRisks.every((risk) => !risk.includes(","))
      ? rawRisks
      : [];
    setSelectedRisks(risksFromUrl);
  }, [searchParams]);

  const handleTabChange = (nextTab) => {
    const nextParams = new URLSearchParams(location.search);
    if (nextTab === "overview") {
      nextParams.delete("tab");
    } else {
      nextParams.set("tab", "all");
    }

    navigate(`${location.pathname}?${nextParams.toString()}`, { replace: true });
  };

  // Extracting unique responsibles using flatMap for consistent handling
  const uniqueResponsibles = improvementProjectsData
    .flatMap((project) =>
      Array.isArray(project.responsible)
        ? project.responsible
        : [project.responsible],
    )
    .filter(Boolean)
    .filter((value, index, self) => self.indexOf(value) === index)
    .map((responsible) => ({ id: responsible, label: responsible }));

  const uniqueStatuses = improvementProjectsData
    .flatMap((project) =>
      Array.isArray(project.status) ? project.status : [project.status],
    )
    .filter(Boolean)
    .filter((value, index, self) => self.indexOf(value) === index)
    .map((status) => ({ id: status, label: status }));

  const getProjectRisks = (project) => {
    const linkedFindingKeys = project?.linked_findings || [];

    return linkedFindingKeys
      .map((findingKey) => {
        const finding = Object.values(findingsData || {}).find(
          (f) => f.key === findingKey,
        );
        return finding?.risk;
      })
      .filter((risk) => risk && risk.risk_id)
      .filter(
        (risk, index, self) =>
          self.findIndex((r) => r.risk_id === risk.risk_id) === index,
      )
      .sort((a, b) => a.risk_id.localeCompare(b.risk_id))
      .map((risk) => risk.risk_id);
  };

  const uniqueRisks = improvementProjectsData
    .flatMap((project) => getProjectRisks(project))
    .filter(Boolean)
    .filter((value, index, self) => self.indexOf(value) === index)
    .map((riskId) => ({ id: riskId, label: riskId }))
    .sort((a, b) => a.id.localeCompare(b.id));

  const handleResponsibleFilterChange = (newSelectedValues) => {
    setSelectedResponsibles(newSelectedValues);
  };

  const handleStatusFilterChange = (newSelectedValues) => {
    setSelectedStatuses(newSelectedValues);

    const newSearchParams = new URLSearchParams(location.search);
    newSearchParams.delete("status");
    newSelectedValues.forEach((status) => {
      newSearchParams.append("status", status);
    });

    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    });
  };

  const handleRiskFilterChange = (newSelectedValues) => {
    setSelectedRisks(newSelectedValues);

    const newSearchParams = new URLSearchParams(location.search);

    newSearchParams.delete("risk");
    newSelectedValues.forEach((risk) => {
      newSearchParams.append("risk", risk);
    });

    navigate(`${location.pathname}?${newSearchParams.toString()}`, {
      replace: true,
    });
  };

  const handleDateRangeFilterChange = (newDateRange) => {
    setDateRange(newDateRange);
  };

  const handleSelectedRowsChange = (selectedRows) => {
    setSelectedImprovementProjects(selectedRows);
  };

  const handleExportSelectedImprovementProjects = async () => {
    if (!selectedImprovementProjects.length) {
      showActionToast(
        t("improvements.actionToast.noneSelectedTitle"),
        t("improvements.actionToast.exportNoneSelectedText"),
      );
      return;
    }

    setLoading(true);
    setLoadingAction("export");

    await handleExport({
      selectedItems: selectedImprovementProjects,
      orgId,
      sessionToken,
      getKey: (item) => item.key,
      filePrefix: "Improvement Project",
    });

    setLoading(false);
    setLoadingAction("");
  };

  const handleBulkArchive = async () => {
    setShowConfirmationPopup(false);

    if (!selectedImprovementProjects.length) {
      setLoading(false);
      setLoadingAction("");
      return;
    }

    const payload = selectedImprovementProjects.map((project) => ({
      id: project.id,
      archived: true,
    }));

    const archivedKeys = selectedImprovementProjects.map(
      (project) => project.key,
    );

    setLoading(true);
    setLoadingAction("archive");
    dispatch(showOverlay());

    try {
      await axiosInstance.patch(
        `/nocodb/improvements?org_id=${orgId}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "session-token": sessionToken,
          },
        },
      );

      // Dispatch updated organization data
      await dispatch(
        getOrganizationData(
          orgId,
          sessionToken,
          navigate,
          false,
          false,
          true,
          false,
          false,
          false,
        ),
      );

      // Show success alert
      Swal.fire({
        toast: true,
        icon: "success",
        title: t("improvements.swal.archiveSuccessTitle"),
        text: t("improvements.swal.archiveSuccessText", {
          keys: archivedKeys.join(", ")
        }),
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } catch (error) {
      console.error("Error archiving improvements:", error);
      Swal.fire({
        toast: true,
        icon: "error",
        title: t("improvements.swal.archiveFailedTitle"),
        text: t("improvements.swal.archiveFailedText"),
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setLoading(false);
      setLoadingAction("");
      dispatch(hideOverlay()); // Hide overlay

      // Trigger row selection reset
      dispatch(resetSelection());
      setTimeout(() => dispatch(clearResetSelection()), 100); // Reset to false after a short delay
    }
  };

  const handleBulkEdit = () => {
    if (selectedImprovementProjects.length === 0) {
      showActionToast(
        t("improvements.actionToast.noneSelectedTitle"),
        t("improvements.actionToast.bulkEditNoneSelectedText"),
      );
      return;
    }

    if (selectedImprovementProjects.length < 2) {
      showActionToast(
        t("improvements.actionToast.notEnoughSelectedTitle"),
        t("improvements.actionToast.bulkEditMinSelectedText"),
      );
      return;
    }

    dispatch(
      showModal({
        name: "bulk_edit_modal",
        data: selectedImprovementProjects,
      }),
    );
  };

  const handleCreateProjectClick = () => {
    dispatch(showModal({ name: "create_improvement_project_modal" }));
  };

  const handleClearSelection = () => {
    dispatch(resetSelection());
    window.setTimeout(() => dispatch(clearResetSelection()), 100);
    setSelectedImprovementProjects([]);
  };

  const filteredData = visibleImprovementProjects
    .filter((item) => {
      const {
        responsible,
        accountable,
        consulted,
        informed,
        status,
        date: itemDate,
      } = item;

      const dueDate = new Date(itemDate);
      const formattedStartDate = dateRange[0];
      const formattedEndDate = dateRange[1];

      // Convert search query to lowercase for consistent comparison
      const searchQueryLower = searchQuery.toLowerCase();

      // Responsible match logic handling both arrays and single values
      const responsibleMatch =
        selectedResponsibles.length === 0 ||
        (Array.isArray(responsible)
          ? responsible.some((r) => selectedResponsibles.includes(r))
          : selectedResponsibles.includes(responsible));

      // Status match using labels directly
      const statusMatch =
        selectedStatuses.length === 0 || selectedStatuses.includes(status);

      const riskIds = getProjectRisks(item);
      const riskMatch =
        selectedRisks.length === 0 ||
        riskIds.some((riskId) => selectedRisks.includes(riskId));

      // Date range match
      const dateMatch =
        (!formattedStartDate && !formattedEndDate) ||
        (dueDate >= formattedStartDate && dueDate <= formattedEndDate);

      // Search query match
      const searchMatch =
        !searchQueryLower ||
        item.key?.toLowerCase().includes(searchQueryLower) ||
        item.name?.toLowerCase().includes(searchQueryLower) ||
        item.description?.toLowerCase().includes(searchQueryLower) ||
        riskIds.some((riskId) =>
          riskId.toLowerCase().includes(searchQueryLower),
        ) ||
        (Array.isArray(responsible)
          ? responsible.some((r) => r.toLowerCase().includes(searchQueryLower))
          : responsible?.toLowerCase().includes(searchQueryLower)) ||
        status?.toString().toLowerCase().includes(searchQueryLower);

      // Me mode filter: only show projects where the current user is responsible, accountable, consulted & informed
      const meModeMatch =
        !meModeActive ||
        [responsible, accountable, consulted, informed].some((role) =>
          Array.isArray(role)
            ? role.includes(currentUser)
            : role === currentUser,
        );

      return [
        responsibleMatch,
        statusMatch,
        riskMatch,
        dateMatch,
        searchMatch,
        meModeMatch,
      ].every(Boolean);
    });

  const mapProjectToOverviewBucket = (status) => {
    const normalized = status?.toString?.().trim?.().toLowerCase?.();
    if (!normalized) return "notStarted";

    if (normalized === "done" || normalized === "closed") return "completed";
    if (normalized === "in progress" || normalized === "in review") return "active";
    if (normalized === "open") return "notStarted";

    return "notStarted";
  };

  const overviewMetrics = useMemo(() => {
    const total = visibleImprovementProjects.length;

    const buckets = visibleImprovementProjects.reduce(
      (acc, project) => {
        const bucket = mapProjectToOverviewBucket(project.status);
        acc[bucket] += 1;
        return acc;
      },
      { notStarted: 0, active: 0, completed: 0 },
    );

    const completionRate = total
      ? Math.round((buckets.completed / total) * 100)
      : 0;

    const statusChart = {
      labels: [
        t("improvements.overview.status.active"),
        t("improvements.overview.status.completed"),
        t("improvements.overview.status.notStarted"),
      ],
      values: [buckets.active, buckets.completed, buckets.notStarted],
      colors: ["#22C55E", "#3B82F6", "#94A3B8"],
    };

    return {
      total,
      active: buckets.active,
      completed: buckets.completed,
      notStarted: buckets.notStarted,
      completionRate,
      statusChart,
    };
  }, [t, visibleImprovementProjects]);

  const StatusDonutChart = ({ labels, values, colors }) => {
    const canvasRef = useRef(null);
    const chartRef = useRef(null);

    useEffect(() => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }

      if (!canvasRef.current) return;
      const total = values.reduce((sum, value) => sum + value, 0);
      if (!total) return;

      const outsideLabelsPlugin = {
        id: "outsideLabels",
        afterDraw: (chart) => {
          const meta = chart.getDatasetMeta(0);
          const arcs = meta?.data || [];
          const dataset = chart.data?.datasets?.[0];
          const rawValues = dataset?.data || [];
          const totalValue = rawValues.reduce(
            (sum, value) => sum + Number(value || 0),
            0,
          );

          if (!totalValue) return;

          const ctx = chart.ctx;
          const chartCenterX = meta.data?.[0]?.x ?? chart.width / 2;
          const isCompact = chart.width < 420;
          const baseFontSize = isCompact ? 12 : 14;
          const offset = isCompact ? 8 : 16;
          const edgePadding = isCompact ? 2 : 4;

          ctx.save();
          ctx.font = `600 ${baseFontSize}px ABC Whyte Inktrap, Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial`;
          ctx.textBaseline = "middle";

          const safeTop = edgePadding;
          const safeBottom = chart.height - edgePadding;
          const safeLeft = edgePadding;
          const safeRight = chart.width - edgePadding;

          arcs.forEach((arc, index) => {
            const value = Number(rawValues[index] || 0);
            if (!value) return;

            const percent = Math.round((value / totalValue) * 100);
            const label = chart.data.labels?.[index] ?? "";
            const text = `${label} ${percent}%`;
            const sliceColor = Array.isArray(dataset?.backgroundColor)
              ? dataset.backgroundColor[index]
              : dataset?.backgroundColor;

            const angle = (arc.startAngle + arc.endAngle) / 2;
            const x = arc.x + Math.cos(angle) * (arc.outerRadius + offset);
            const y = arc.y + Math.sin(angle) * (arc.outerRadius + offset);

            ctx.fillStyle = sliceColor || "#475569";
            ctx.textAlign = x < chartCenterX ? "right" : "left";

            const metrics = ctx.measureText(text);
            const padding = 8;
            const minX =
              safeLeft + padding + (ctx.textAlign === "right" ? metrics.width : 0);
            const maxX =
              safeRight -
              padding -
              (ctx.textAlign === "left" ? metrics.width : 0);
            const clampedX = Math.min(maxX, Math.max(minX, x));
            const minY = safeTop + baseFontSize / 2 + 4;
            const maxY = safeBottom - baseFontSize / 2 - 4;
            const clampedY = Math.min(maxY, Math.max(minY, y));

            ctx.fillText(text, clampedX, clampedY);
          });

          ctx.restore();
        },
      };

      chartRef.current = new Chart(canvasRef.current, {
        type: "pie",
        data: {
          labels,
          datasets: [
            {
              data: values,
              backgroundColor: colors,
              borderColor: "#FFFFFF",
              borderWidth: 1,
              radius: "75%",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              top: 80,
              left: 140,
              right: 140,
              bottom: 80,
            },
          },
          plugins: {
            legend: {
              display: true,
              position: "bottom",
              labels: {
                boxWidth: 10,
                boxHeight: 10,
                padding: 12,
                color: "#1F2937",
                font: { size: 14, weight: "400" },
              },
            },
            tooltip: {
              enabled: true,
              displayColors: false,
              callbacks: {
                title: () => "",
                label: (context) => `${context.label}: ${context.raw}`,
              },
            },
          },
        },
        plugins: [outsideLabelsPlugin],
      });

      return () => {
        chartRef.current?.destroy();
        chartRef.current = null;
      };
    }, [colors, labels, values]);

    return (
      <div className={styles.statusChartCanvas}>
        <canvas ref={canvasRef} />
      </div>
    );
  };

  const handleRowClick = (row) => {
    navigate(
      `/${orgId}/improvement-projects/${row.original.key}${location.search}`,
      {
        replace: false,
      },
    );
  };

  const truncateText = (text, maxLength) => {
    if (text.length <= maxLength) {
      return text;
    }
    return (
      <>
        {text.substring(0, maxLength)}
        {" "}
        <span className={styles.readMore}>{t("improvements.readMore")}</span>
      </>
    );
  };

  const simpleTruncateText = (text, maxLength) => {
    if (text.length <= maxLength) {
      return text;
    }
    return (
      <>
        {text.substring(0, maxLength)}
        <span>...</span>
      </>
    );
  };

  const columns = [
    {
      accessorKey: "select",
      size: 50,
      cell: ({ row }) => (
        <div className="customCheckboxContainer">
          {row.getIsSelected() ? (
            <div className="checked" />
          ) : (
            <div className="unchecked" />
          )}
        </div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "key",
      header: t("improvements.table.key"),
      cell: (props) => (
        <span className={styles.keyPill}>{props.getValue()}</span>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "name",
      header: t("improvements.table.title"),
      cell: (props) => <div>{simpleTruncateText(props.getValue(), 60)}</div>,
      enableSorting: false,
    },
    {
      accessorKey: "risks",
      header: t("improvements.table.risk"),
      cell: (props) => {
        const riskIds = getProjectRisks(props.row.original);
        return (
          <div className={styles.riskPillGroup}>
            {riskIds.length
              ? riskIds.map((riskId) => (
                  <span key={riskId} className={styles.riskPill}>
                    {riskId}
                  </span>
                ))
              : t("improvements.table.notDefined")}
          </div>
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: "description",
      header: t("improvements.table.description"),
      cell: (props) => (
        <div>{props.getValue() ? truncateText(props.getValue(), 80) : t("improvements.table.notDefined")}</div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: "responsible",
      header: t("improvements.table.responsible"),
      cell: (props) => {
        const responsible = props.row.original.responsible;
        const hasResponsible = Array.isArray(responsible)
          ? responsible.length > 0
          : Boolean(responsible);

        return <div>{hasResponsible ? t("improvements.table.assigned") : ""}</div>;
      },
      enableSorting: false,
    },
    {
      accessorKey: "status",
      header: t("improvements.table.status"),
      cell: (props) => {
        const status = props.getValue();
        let statusClassName = "";

        switch (status) {
          case "Open":
            statusClassName = "openStatus";
            break;
          case "In Progress":
            statusClassName = "inProgressStatus";
            break;
          case "In Review":
            statusClassName = "inReviewStatus";
            break;
          case "Done":
            statusClassName = "resolvedStatus";
            break;
          default:
            break;
        }

        return (
          <div className={classNames("statusCell", statusClassName)}>
            {status || t("improvements.table.notDefined")}
          </div>
        );
      },
      enableSorting: false,
    },
    {
      accessorKey: "date",
      header: t("improvements.table.date"),
      cell: (props) => (
        <div>
          {props.getValue() ? formatDateSlash(props.getValue()) : t("improvements.table.notDefined")}
        </div>
      ),
      enableSorting: false,
    },
  ];

  const selectedImprovementProjectsCount = selectedImprovementProjects.length;
  const hasSelectedImprovementProjects = selectedImprovementProjectsCount > 0;
  const selectedImprovementProjectsLabel =
    selectedImprovementProjectsCount === 1
      ? t("improvements.selectionBar.single", {
        count: selectedImprovementProjectsCount,
      })
      : t("improvements.selectionBar.multiple", {
        count: selectedImprovementProjectsCount,
      });

  return (
    <MainLayout>
      <CreateImprovementProjectModal />
      <BulkEditModal modalType="improvements" orgId={orgId} />
      <div className={styles.column}>
        <div className={styles.improvementProjectsContainer}>
          <div className={styles.pageHeader}>
            <div className={styles.headerLeft}>
              <div className={styles.titleGroup}>
                <div className={styles.titleIcon}>
                  <TrendingUp size={32} />
                </div>
                <div className={styles.pageTitle}>
                  {t("modules.improvementprojects")}
                </div>
              </div>
              <div className={styles.pageSubtitle}>
                {t("improvements.subtitle")}
              </div>
            </div>
            <Button
              text={t("improvements.actions.newProject")}
              className={styles.headerAction}
              onClick={handleCreateProjectClick}
              disabled={loading}
              icon={<Plus size={16} />}
              iconPosition="left"
            />
          </div>

          <div className={styles.tabsRow}>
            <div className={styles.tabSwitcher} role="tablist">
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "overview"}
                className={classNames(styles.tabButton, {
                  [styles.tabButtonActive]: activeTab === "overview",
                })}
                onClick={() => handleTabChange("overview")}
              >
                {t("improvements.tabs.overview")}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === "all"}
                className={classNames(styles.tabButton, {
                  [styles.tabButtonActive]: activeTab === "all",
                })}
                onClick={() => handleTabChange("all")}
              >
                {t("improvements.tabs.allProjects")}
              </button>
            </div>
          </div>

          {activeTab === "overview" && (
            <>
              <div className={styles.actionsRow}>
                <button
                  className={styles.actionGhostButton}
                  onClick={handleBulkEdit}
                  disabled={loading}
                  type="button"
                >
                  <Edit2 size={16} />
                  {t("improvements.actions.bulkEdit")}
                </button>
                <button
                  className={styles.actionGhostButton}
                  onClick={() => {
                    if (!selectedImprovementProjects.length) {
                      showActionToast(
                        t("improvements.actionToast.noneSelectedTitle"),
                        t("improvements.actionToast.archiveNoneSelectedText"),
                      );
                      return;
                    }

                    setShowConfirmationPopup(true);
                  }}
                  disabled={loading}
                  type="button"
                >
                  <Archive size={16} />
                  {loading && loadingAction === "archive"
                    ? t("improvements.actions.archiving")
                    : t("improvements.actions.archive")}
                </button>
                <button
                  className={styles.actionGhostButton}
                  onClick={handleExportSelectedImprovementProjects}
                  disabled={loading}
                  type="button"
                >
                  <FileText size={16} />
                  {loading && loadingAction === "export"
                    ? t("improvements.actions.exporting")
                    : t("improvements.actions.export")}
                </button>
              </div>

              {actionToast && (
                <div
                  className={styles.bottomToast}
                  role="alert"
                  aria-live="assertive"
                >
                  <div className={styles.bottomToastTitle}>
                    {actionToast.title}
                  </div>
                  <div className={styles.bottomToastText}>
                    {actionToast.text}
                  </div>
                </div>
              )}

              <div className={styles.filtersRow}>
                <MultiSelect
                  label={
                    selectedResponsibles.length > 0
                      ? selectedResponsibles.join(", ")
                      : t("improvements.filters.responsible")
                  }
                  options={uniqueResponsibles}
                  onChangeSelectedValues={handleResponsibleFilterChange}
                  selectedValues={selectedResponsibles}
                  disabled={!improvementProjectsData.length}
                  className={styles.multiselect}
                />
                <MultiSelect
                  label={
                    selectedStatuses.length > 0
                      ? selectedStatuses.join(", ")
                      : t("improvements.filters.status")
                  }
                  options={uniqueStatuses}
                  selectedValues={selectedStatuses}
                  onChangeSelectedValues={handleStatusFilterChange}
                  disabled={!improvementProjectsData.length}
                  className={styles.multiselect}
                />
                <MultiSelect
                  label={
                    selectedRisks.length > 0
                      ? selectedRisks.join(", ")
                      : t("improvements.filters.risk")
                  }
                  options={uniqueRisks}
                  selectedValues={selectedRisks}
                  onChangeSelectedValues={handleRiskFilterChange}
                  disabled={!improvementProjectsData.length}
                  className={styles.multiselect}
                />
                <DateRangeFilter
                  handleDateRangeChange={handleDateRangeFilterChange}
                  dateRange={dateRange}
                  isClearable
                  className={styles.improvementDateFilter}
                />
                <Input
                  type="text"
                  placeholder={t("improvements.filters.search")}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={styles.input}
                  icon={SearchIcon}
                  wrapperClassName={styles.searchWrapper}
                />
              </div>

              {hasSelectedImprovementProjects && (
                <div className={styles.selectionBar}>
                  <div className={styles.selectionMeta}>
                    <span className={styles.selectionCount}>
                      {selectedImprovementProjectsLabel}
                    </span>
                    <button
                      type="button"
                      className={styles.selectionClearButton}
                      onClick={handleClearSelection}
                    >
                      {t("improvements.selectionBar.clear")}
                    </button>
                  </div>
                </div>
              )}

              <div
                className={classNames(
                  styles.tableCard,
                  !filteredData.length && styles.tableCardEmpty,
                )}
              >
                <Table
                  columns={columns}
                  data={filteredData}
                  onRowClick={handleRowClick}
                  onSelectedRowsChange={handleSelectedRowsChange}
                  rowHover
                  initialSorting={[{ id: "date", desc: true }]}
                  resetSelection={resetRowSelection}
                  variant="improvementProjects"
                />
                {!filteredData.length && (
                  <div className={styles.tableEmptyState}>
                    <div className={styles.tableEmptyContent}>
                      <div className={styles.tableEmptyCopy}>
                        {t("improvements.noData")}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {activeTab === "all" && (
            <div className={styles.overviewSection}>
              <div className={styles.metricGrid}>
                <div className={styles.metricCard}>
                  <div className={styles.metricHeader}>
                    <span className={styles.metricTitle}>
                      {t("improvements.overview.cards.totalProjects")}
                    </span>
                    <span
                      className={classNames(
                        styles.metricIconBadge,
                        styles.metricIconNeutral,
                      )}
                    >
                      <TrendingUp size={18} />
                    </span>
                  </div>
                  <div className={styles.metricValue}>{overviewMetrics.total}</div>
                </div>

                <div className={styles.metricCard}>
                  <div className={styles.metricHeader}>
                    <span className={styles.metricTitle}>
                      {t("improvements.overview.cards.active")}
                    </span>
                    <span
                      className={classNames(
                        styles.metricIconBadge,
                        styles.metricIconActive,
                      )}
                    >
                      <Clock size={18} />
                    </span>
                  </div>
                  <div className={styles.metricValue}>{overviewMetrics.active}</div>
                </div>

                <div className={styles.metricCard}>
                  <div className={styles.metricHeader}>
                    <span className={styles.metricTitle}>
                      {t("improvements.overview.cards.completed")}
                    </span>
                    <span
                      className={classNames(
                        styles.metricIconBadge,
                        styles.metricIconSuccess,
                      )}
                    >
                      <CheckCircle2 size={18} />
                    </span>
                  </div>
                  <div className={styles.metricValue}>{overviewMetrics.completed}</div>
                  <div className={styles.metricSubtitle}>
                    {t("improvements.overview.cards.completionRate", {
                      rate: overviewMetrics.completionRate,
                    })}
                  </div>
                </div>

                <div className={styles.metricCard}>
                  <div className={styles.metricHeader}>
                    <span className={styles.metricTitle}>
                      {t("improvements.overview.cards.notStarted")}
                    </span>
                    <span
                      className={classNames(
                        styles.metricIconBadge,
                        styles.metricIconWarning,
                      )}
                    >
                      <AlertCircle size={18} />
                    </span>
                  </div>
                  <div className={styles.metricValue}>{overviewMetrics.notStarted}</div>
                </div>
              </div>

              <div className={styles.chartsGrid}>
                <div className={styles.chartCard}>
                  <div className={styles.chartHeader}>
                    {t("improvements.overview.charts.projectsByStatus")}
                  </div>
                  {overviewMetrics.total ? (
                    <div className={styles.statusChartBody}>
                      <StatusDonutChart
                        labels={overviewMetrics.statusChart.labels}
                        values={overviewMetrics.statusChart.values}
                        colors={overviewMetrics.statusChart.colors}
                      />
                    </div>
                  ) : (
                    <div className={styles.chartEmptyState}>
                      {t("improvements.overview.empty")}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {showConfirmationPopup && (
        <ConfirmationPopup
          message={t("improvements.popup.archiveMessage")}
          illustration={ArchiveIllustration}
          confirmText={t("improvements.popup.confirm")}
          onConfirm={handleBulkArchive}
          onCancel={() => setShowConfirmationPopup(false)}
        />
      )}
    </MainLayout>
  );
};

export default ImprovementProjects;
